import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getConsent, onReopenRequest, setConsent } from '@/lib/cookie-consent'

// Microsoft Clarity (src/components/clarity-analytics.tsx) sets first-party
// cookies as soon as it loads. Without a gate here, that happened on every
// visit, before anyone had a chance to say yes -- non-essential cookies set
// ahead of consent, which is what a GDPR/ePrivacy-minded scan flags. This
// banner is that gate: Clarity now only loads once "accepted" is stored,
// and never loads at all if declined.
export function CookieConsent() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    setVisible(getConsent() === null)
    return onReopenRequest(() => setVisible(true))
  }, [])

  if (!visible) return null

  function choose(value: 'accepted' | 'declined') {
    setConsent(value)
    setVisible(false)
  }

  return (
    <div
      role="region"
      aria-label="Cookie notice"
      className="fixed inset-x-0 bottom-[60px] z-50 border-t border-n-200 bg-n-100 px-4 py-4 min-[900px]:bottom-0 min-[900px]:px-8"
    >
      <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-8 gap-y-4">
        <p className="max-w-[60ch] text-body text-n-500">
          This site uses Microsoft Clarity to see which pages are working, but only once you say
          it's okay -- no cookies are set until you choose.{' '}
          <Link
            to="/privacy-policy"
            className="text-ink underline decoration-n-200 underline-offset-4 hover:text-flow hover:decoration-flow"
          >
            Privacy Policy
          </Link>
        </p>
        <div className="ml-auto flex gap-3">
          <button
            type="button"
            onClick={() => choose('declined')}
            className="flex min-h-11 items-center rounded-lg border border-n-200 px-5 text-body font-medium text-ink transition-colors hover:bg-n-200"
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => choose('accepted')}
            className="flex min-h-11 items-center rounded-lg bg-flow px-5 text-body font-semibold text-n-100 transition-colors hover:bg-flow-hi"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}
